import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tripAPI } from '../../api/trips';
import { Trip } from '../../types/entities';
import { Plus, Eye, Edit, Trash2, Search, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { DateRangeQuickFilter } from '../../components/UI/DateRangeQuickFilter';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    'PENDING': 'bg-yellow-100 text-yellow-700',
    'IN_PROGRESS': 'bg-blue-100 text-blue-700',
    'COMPLETED': 'bg-green-100 text-green-700',
    'CANCELLED': 'bg-red-100 text-red-700',
  };
  return styles[status] || 'bg-gray-100 text-gray-700';
};

const getTripTypeBadge = (tripType: string) => {
  if (tripType === 'IMPORT') {
    return {
      icon: <ArrowDownToLine className="w-3 h-3" />,
      className: 'bg-blue-100 text-blue-700',
    };
  }
  return {
    icon: <ArrowUpFromLine className="w-3 h-3" />,
    className: 'bg-green-100 text-green-700',
  };
};

export const TripList: React.FC = () => {
  const { t } = useTranslation();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    tripType: '' as '' | 'IMPORT' | 'EXPORT',
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadTrips();
  }, []);

  const loadTrips = async () => {
    try {
      const filterParams: any = {};
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      if (filters.tripType) filterParams.tripType = filters.tripType;

      const data = await tripAPI.getAll(
        Object.keys(filterParams).length > 0 ? filterParams : undefined
      );
      setTrips(data);
    } catch (error) {
      toast.error(t('trips.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    loadTrips();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('trips.deleteConfirm'))) return;

    try {
      await tripAPI.delete(id);
      toast.success(t('trips.deleteSuccess'));
      loadTrips();
    } catch (error) {
      toast.error(t('trips.deleteError'));
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('trips.title')}</h1>
        <button
          onClick={() => navigate('/trips/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('trips.addNew')}
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangeQuickFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
            onEndDateChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
          />
          <div>
            <label className="block text-sm font-medium mb-1">{t('trips.tripType')}</label>
            <select
              value={filters.tripType}
              onChange={(e) => setFilters(prev => ({ ...prev, tripType: e.target.value as any }))}
              className="input-field"
            >
              <option value="">{t('common.all')}</option>
              <option value="IMPORT">{t('trips.import')}</option>
              <option value="EXPORT">{t('trips.export')}</option>
            </select>
          </div>
          <button onClick={handleSearch} className="btn-primary flex items-center">
            <Search className="w-4 h-4 mr-2" />
            {t('common.search')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('reports.tripId')}</th>
              <th className="pb-3 table-header">{t('trips.tripType')}</th>
              <th className="pb-3 table-header">{t('trips.date')}</th>
              <th className="pb-3 table-header">{t('trips.vehicle')}</th>
              <th className="pb-3 table-header">{t('trips.pickupPoint')}</th>
              <th className="pb-3 table-header">{t('trips.deliveryPoint')}</th>
              <th className="pb-3 table-header text-right">{t('trips.totalValue')}</th>
              <th className="pb-3 table-header">{t('common.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {trips.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              trips.map((trip) => {
                const tripTypeBadge = getTripTypeBadge(trip.tripType || 'EXPORT');
                // Get pickup/delivery names based on type
                const pickupName = trip.pickupType === 'WAREHOUSE'
                  ? (trip as any).pickupWarehouse?.name
                  : trip.pickupType === 'CUSTOMER'
                    ? trip.customer?.name
                    : trip.supplier?.name;
                const deliveryName = trip.deliveryType === 'WAREHOUSE'
                  ? (trip as any).deliveryWarehouse?.name
                  : trip.deliveryType === 'SUPPLIER'
                    ? trip.supplier?.name
                    : trip.customer?.name;

                return (
                  <tr key={trip.id} className="border-b last:border-0">
                    <td className="py-3 font-medium">#{trip.id}</td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${tripTypeBadge.className}`}>
                        {tripTypeBadge.icon}
                        {t(`trips.${(trip.tripType || 'EXPORT').toLowerCase()}`)}
                      </span>
                    </td>
                    <td className="py-3">
                      {format(new Date(trip.date), 'dd/MM/yyyy')}
                    </td>
                    <td className="py-3">{trip.vehicle?.licensePlate || '-'}</td>
                    <td className="py-3">
                      <div className="text-sm">
                        <span className="text-gray-500 text-xs">{t(`entity.${(trip.pickupType || 'SUPPLIER').toLowerCase()}`)}</span>
                        <br />
                        {pickupName || trip.supplier?.name || '-'}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="text-sm">
                        <span className="text-gray-500 text-xs">{t(`entity.${(trip.deliveryType || 'CUSTOMER').toLowerCase()}`)}</span>
                        <br />
                        {deliveryName || trip.customer?.name || '-'}
                      </div>
                    </td>
                    <td className="py-3 text-right font-medium">
                      {formatCurrency(trip.totalValue)} đ
                    </td>
                    <td className="py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(trip.status)}`}>
                          {t(`trips.statuses.${trip.status}`)}
                        </span>
                        {/* Phase 2: Warehouse receipt status badge */}
                        {trip.warehouseReceiptStatus === 'PENDING' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                            {t('warehouse.receiptStatus.pending')}
                          </span>
                        )}
                        {trip.warehouseReceiptStatus === 'RECEIPTED' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                            {t('warehouse.receiptStatus.receipted')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <Link
                          to={`/trips/${trip.id}`}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                          title={t('trips.viewTrip')}
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => navigate(`/trips/${trip.id}/edit`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title={t('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(trip.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
